/**
 * @description Pack SO Company identity / Company Settings (LYN-356, LYN-362, LYN-363)
 *
 * Key features:
 * - LynxPay merchant Company cut down to identity + Company Settings
 * - No Transactions / Invoicing chrome
 * - Company Settings SoT is GET / PUT /companies/me via useCompany (same as
 *   LynxPay CompanyDashboard). /auth/me is session context, not this form.
 * - Settings save closes only when updateCompany returns success
 *
 * @dependencies hooks/useCompany, CompanySettingsModal, layouts/so/PackPage
 *
 * @notes Hidden on All (LYN-357). Extra site rows stay later.
 *   Do not hydrate from Auth CompanyContext or skip loadCompany when /me
 *   already has a company — that blob is slim (no businessType / accountingMethod).
 *   PackPage supplies the Company h1; the identity card is not a second heading.
 */

import { useEffect, useState } from 'react';
import { Building2, Settings } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePackChrome } from '../../contexts/PackChromeContext';
import { useCompany } from '../../hooks/useCompany';
import CompanySettingsModal from '../../components/company/settings/CompanySettingsModal';
import PackPage from '../../components/layouts/so/PackPage';
import { getBusinessTypeLabel } from '../../constants/businessTypes';

export default function SoCompanyPage() {
  const { towtechSoChrome } = usePackChrome();
  const { companyScope } = useAuth();
  const {
    company,
    hasCompany,
    loading,
    error,
    loadCompany,
    updateCompany
  } = useCompany();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!company) {
      loadCompany();
    }
  }, [company, loadCompany]);

  const handleUpdateCompany = async (formData) => {
    setIsUpdating(true);
    setUpdateError(null);
    const result = await updateCompany(formData);
    if (result?.success === true) {
      setIsSettingsOpen(false);
    } else {
      setUpdateError(result?.error || 'Failed to update company');
    }
    setIsUpdating(false);
    return result;
  };

  if (towtechSoChrome && companyScope === 'all') {
    return <Navigate to="/so/dashboard" replace />;
  }

  if (loading && !company) {
    return <div className="p-6 text-sm text-gray-500">Loading company…</div>;
  }

  if (error && !company) {
    return <div className="p-6 text-sm text-red-600">{error}</div>;
  }

  if (!company) {
    // useCompany.checkCompanyStatus finishes with company === null until GET /companies/me.
    if (hasCompany || loading) {
      return <div className="p-6 text-sm text-gray-500">Loading company…</div>;
    }
    return <div className="p-6 text-sm text-gray-500">No company yet.</div>;
  }

  return (
    <PackPage title="Company">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="bg-gray-100 rounded-xl p-3">
              <Building2 className="h-6 w-6 text-gray-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{company.businessName}</p>
              <p className="text-sm text-gray-600 mt-1">
                {company.businessType ? getBusinessTypeLabel(company.businessType) : 'Company'}
                {company.city && company.state ? ` • ${company.city}, ${company.state}` : ''}
              </p>
              {company.addressLine1 && (
                <p className="text-sm text-gray-500 mt-2">
                  {company.addressLine1}
                  {company.addressLine2 ? `, ${company.addressLine2}` : ''}
                  {company.postalCode ? ` ${company.postalCode}` : ''}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsSettingsOpen(true)}
            className="flex items-center justify-center px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            <Settings className="h-4 w-4 mr-2" />
            Company Settings
          </button>
        </div>
      </div>

      <CompanySettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        company={company}
        onUpdateCompany={handleUpdateCompany}
        isUpdating={isUpdating}
        updateError={updateError}
        showMerchantGlobalSettings={false}
      />
    </PackPage>
  );
}
